import React, { Component } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  EPaymentStatus,
  Order,
  OrderItem,
  OrderItemConfiguration,
} from "../../../classes/types/order/order";
import { Link, useNavigate } from "react-router-dom";
import { KeyboardArrowUp, KeyboardArrowDown, Payment, Logout } from "@mui/icons-material";
import "./order.css";
import { OrderHandler } from "../../../classes/methods/orderMethods/orderMethods";
import { AuthHandler } from "../../../classes/methods/authMethods/authMethods";
import Paper from "@mui/material/Paper";
import LoginEvent from "../../../classes/events/LoginEvent";
import dayjs from "dayjs";
import { euroPrint } from "../../../classes/types/currencysettings";
import PaymentHandler, { PaymentIntent } from "../../../classes/methods/paymentMethods/paymentMethods";
import { makeStyles } from "@mui/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import PaymentMethodBadge from "../../customComponents/payment-method-badge/PaymentMethodBadge";
import CartEvents from "../../../classes/events/CartEvents";
import { GammaDocument, GammaDocumentRow } from "../../../classes/types/gamma-document/GammaDocument";
import DocumentHandler from "../../../classes/methods/documentMethods/DocumentMethods";
import GammaEvasionBadge from "../../customComponents/gammaEvasionBadge/GammaEvasionBadge";
import { User } from "../../../classes/types/user/user";
import { EnumTranslator } from "../../../classes/utilities/enumTranslator";

// --- Styles ---
const useStyles1 = makeStyles((theme: any) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

// --- Component State ---
interface OrdersState {
  orders: Array<Order>;
  documentsPerPage: number;
  gammaDocuments: Array<GammaDocument>;
  showConfirmPaymentDialog: boolean;
  verifyingOrder: boolean;
  returnedOrderFromVerification: Order;
  currentPage: number;
  currentTab: number;
  numberPerPage: number;
  loading: boolean;
  currentUser: User;
  currentConfiguration: Array<OrderItemConfiguration>;
  showConfiguration: boolean;
}

// --- Class Component ---
interface OrdersProps {
  navigate: (path: string) => void;
}

class OrdersClass extends Component<OrdersProps, OrdersState> {
  constructor(props: OrdersProps) {
    super(props);
    this.state = {
      orders: [],
      gammaDocuments: [],
      showConfirmPaymentDialog: false,
      verifyingOrder: false,
      returnedOrderFromVerification: new Order(),
      currentPage: 0,
      currentTab: 0,
      documentsPerPage: 10,
      numberPerPage: 10,
      loading: true,
      currentUser: new User(),
      currentConfiguration: [],
      showConfiguration: false,
    };
  }

  componentDidMount() {
    this.refreshData();
  }

  refreshData = () => {
    this.setState({ loading: true });
    AuthHandler.isLogged().then((isLogged) => {
      if (!isLogged) {
        LoginEvent.callLoginEvents();
        CartEvents.callCartModifiedEvents();
        this.props.navigate("/login");
      } else {
        OrderHandler.getOrders()
          .then((newOrders) => {
            newOrders.forEach((order) => {
              order.items.forEach((item) => {
                item.orderItemConfigurations.sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
              });
            });
            DocumentHandler.getDocuments().then((documents) => {
              // const filteredDocs: Array<GammaDocument> = [];
              // documents.forEach((doc) => {
              //   let isNotComplete = false;
              //   doc.rows.forEach((row) => {
              //     if (!row.complete) isNotComplete = true;
              //   });
              //   if (isNotComplete) filteredDocs.push(doc);
              // });
              this.setState({
                orders: newOrders,
                gammaDocuments: documents.sort(
                  (doc1, doc2) => Date.parse(doc2.date) - Date.parse(doc1.date)
                ),
                numberPerPage: newOrders.length < 10 ? newOrders.length : 20,
                documentsPerPage: documents.length < 10 ? documents.length : 20,
                loading: false,
                currentUser: isLogged,
              });
            });
          })
          .catch(() => this.setState({ loading: false }));
      }
    });
  };

  onLogOut = () => {
    AuthHandler.logOut()
      .then(() => {
        LoginEvent.callLoginEvents();
        CartEvents.callCartModifiedEvents();
        this.props.navigate("/");
      })
      .catch(() => { });
  };

  handleChangePage = (event: any, newPage: number) => {
    this.setState({ currentPage: newPage });
  };

  handleFirstPageButtonClick = () => this.setState({ currentPage: 0 });
  handleBackButtonClick = () => this.setState({ currentPage: this.state.currentPage - 1 });
  handleNextButtonClick = () => this.setState({ currentPage: this.state.currentPage + 1 });
  handleLastPageButtonClick = () =>
    this.setState({
      currentPage: Math.ceil(this.state.orders.length / this.state.numberPerPage) - 1,
    });

  TablePaginationActions = () => {
    const styles = useStyles1();
    const theme = useTheme();
    return (
      <div className={styles.root}>
        <IconButton onClick={this.handleFirstPageButtonClick} disabled={this.state.currentPage === 0}>
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton onClick={this.handleBackButtonClick} disabled={this.state.currentPage === 0}>
          {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={this.handleNextButtonClick}
          disabled={this.state.currentPage >= Math.ceil(this.state.orders.length / this.state.numberPerPage) - 1}
        >
          {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton
          onClick={this.handleLastPageButtonClick}
          disabled={this.state.currentPage >= Math.ceil(this.state.orders.length / this.state.numberPerPage) - 1}
        >
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </div>
    );
  };

  showConfiguration = (item: OrderItem) => {
    this.setState({ currentConfiguration: item.orderItemConfigurations }, () => {
      this.setState({ showConfiguration: true });
    });
  };

  onTabChange = (newVal: number) => {
    this.setState({ currentTab: newVal, currentPage: 0 });
  };

  handlePayOrder = (order: Order) => {
    if (order.paymentStatus === EPaymentStatus.Paid) return;

    this.setState({ showConfirmPaymentDialog: true, verifyingOrder: true });

    PaymentHandler.verifyPayment(order.id).then((order: Order) => {
      this.setState({ verifyingOrder: false, returnedOrderFromVerification: order });
    });
  };

  handleCloseDialog = () => {
    this.setState({ showConfirmPaymentDialog: false });
  };

  render() {
    const { orders, gammaDocuments, loading, currentTab, currentPage, numberPerPage, documentsPerPage, currentUser } = this.state;
    return (
      <div style={{ paddingBottom: "2%", marginLeft: "2%", marginRight: "2%" }}>
        <Grid container spacing={2} style={{ gap: '2%', flexWrap: 'nowrap' }}>
          {/* Account Panel */}
          <Grid style={{ marginTop: "1.5%", marginBottom: "2%", width: '30%' }} justifyContent={"flex-start"} alignContent={"flex-start"} md={3} lg={2} item container direction={"column"}>
            <Typography variant="h5" style={{ fontWeight: "bold", textAlign: "left" }}>
              Account
            </Typography>
            <Typography variant="subtitle1" style={{ fontWeight: 400, textAlign: "left", marginBottom: "5%" }}>
              {currentUser.email}
            </Typography>
            <Typography variant="subtitle1" style={{ fontWeight: "bold", textAlign: "left", marginBottom: "5%" }}>
              Il tuo consulente commerciale è
              <br />
              <span style={{ fontWeight: 400 }}>{currentUser.agentName}</span>
              <br />
              <a
                style={{ fontWeight: 400 }}
                color="primary"
                target="_blank"
                rel="noopener noreferrer"
                href={`mailto:${currentUser.agentEmail}`}
              >
                {currentUser.agentEmail}
              </a>
              <br />
              <a
                style={{ fontWeight: 400 }}
                color="primary"
                target="_blank"
                rel="noopener noreferrer"
                href={`tel:${currentUser.agentPhone}`}
              >
                {currentUser.agentPhone}
              </a>
            </Typography>
            <Typography variant="subtitle1" style={{ fontWeight: "bold", textAlign: "left" }}>
              Il tuo metodo di pagamento predefinito è
            </Typography>
            <span style={{ fontWeight: 400, textAlign: "left" }}>{currentUser.registeredPaymentMethod}</span>
            <div style={{ marginBottom: "5%" }} />
            <Button startIcon={<Logout />} onClick={this.onLogOut} color="primary">
              Effettua logout
            </Button>
          </Grid>

          {/* Orders Panel */}
          <Grid style={{ marginTop: "1.5%", marginBottom: "2%", width: '65%' }}
            justifyContent={"flex-start"}
            alignContent={"flex-start"} md={9} lg={10} item container direction={"column"}>
            <Typography variant="h5" style={{ fontWeight: "bold", textAlign: "left", marginBottom: "2%" }}>
              I Miei Ordini
            </Typography>
            <Tabs value={currentTab} onChange={(event, val: number) => this.onTabChange(val)}>
              <Tab label="ORDINI DA CONFERMARE" />
              <Tab label="ORDINI CONFERMATI" />
            </Tabs>

            {/* ORDINI DA CONFERMARE */}
            {currentTab === 0 ? (
              loading ? (
                <CircularProgress />
              ) : (
                <TableContainer
                  style={{ boxShadow: "3px 8px 15px 0.1px #ECECEC", borderRadius: "10px", border: "1px solid lightGrey" }}
                  component={Paper}
                >
                  <Table aria-label="collapsible table">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Data di creazione</TableCell>
                        <TableCell>Metodo di pagamento</TableCell>
                        <TableCell>Indirizzo di spedizione</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell>Totale</TableCell>
                        <TableCell align="right">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders
                        .slice(currentPage * numberPerPage, currentPage * numberPerPage + numberPerPage)
                        .map((order, index) => (
                          <OrderRow key={index} order={order} showConfiguration={this.showConfiguration} handlePayOrderCallback={this.handlePayOrder} />
                        ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          count={orders.length}
                          rowsPerPage={numberPerPage}
                          page={currentPage}
                          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                          onPageChange={this.handleChangePage}
                          ActionsComponent={this.TablePaginationActions}
                          rowsPerPageOptions={[]}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              )
            ) : loading ? (
              <CircularProgress />
            ) : (
              // ORDINI CONFERMATI (Gamma Documents)
              <TableContainer
                style={{ boxShadow: "3px 8px 15px 0.1px #ECECEC", borderRadius: "10px", border: "1px solid lightGrey" }}
                component={Paper}
              >
                <Table aria-label="collapsible table">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Data</TableCell>
                      <TableCell>Numero Documento</TableCell>
                      <TableCell>Evaso</TableCell>
                      <TableCell>Origine</TableCell>
                      <TableCell>Totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gammaDocuments
                      .slice(currentPage * documentsPerPage, currentPage * documentsPerPage + documentsPerPage)
                      .map((document, index) => (
                        <GammaDocumentRowComponent key={index} document={document} />
                      ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        count={gammaDocuments.length}
                        rowsPerPage={numberPerPage}
                        page={currentPage}
                        SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                        onPageChange={this.handleChangePage}
                        ActionsComponent={this.TablePaginationActions}
                        rowsPerPageOptions={[]}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>

        {/* CONFIGURATION DIALOG */}
        <Dialog
          open={this.state.showConfiguration}
          onClose={() => this.setState({ showConfiguration: false })}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Opzioni Selezionate"}</DialogTitle>
          <DialogContent>
            <div className="configurator" hidden={this.state.currentConfiguration.length <= 0}>
              <Divider variant="middle" />
              <TableContainer style={{ width: "100%" }} component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Opzione</TableCell>
                      <TableCell>Quantità</TableCell>
                      <TableCell>Prezzo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.state.currentConfiguration.map((addon, confIndex) => (
                      <TableRow key={confIndex}>
                        <TableCell>{addon.name}</TableCell>
                        <TableCell>{addon.selectedOptionName}</TableCell>
                        <TableCell>{addon.selectedQuantity}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {euroPrint(addon.selectedPrice * addon.selectedQuantity)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </DialogContent>
          <DialogActions>
            <Button color={"primary"} onClick={() => this.setState({ showConfiguration: false })}>
              Chiudi
            </Button>
          </DialogActions>
        </Dialog>

        {/* PAY ORDER DIALOG */}
        <PayOrderDialog
          handleCloseDialog={this.handleCloseDialog}
          loading={this.state.verifyingOrder}
          order={this.state.returnedOrderFromVerification}
          visible={this.state.showConfirmPaymentDialog}
        />
      </div>
    );
  }
}
// --- PayOrderDialog Class ---
interface PayOrderDialogProps {
  visible: boolean;
  order: Order;
  loading: boolean;
  handleCloseDialog: () => void;
}

interface PayOrderDialogState {
  loadingPaymentIntent: boolean;
}

class PayOrderDialog extends Component<PayOrderDialogProps, PayOrderDialogState> {
  constructor(props: PayOrderDialogProps) {
    super(props);
    this.state = { loadingPaymentIntent: false };
  }

  tryPayment = () => {
    this.setState({ loadingPaymentIntent: true });
    PaymentHandler.getPaymentUrl(this.props.order).then((paymentResponse: PaymentIntent) => {
      window.location.replace(paymentResponse.paymentUrl);
    });
  };

  render() {
    const { visible, loading, order, handleCloseDialog } = this.props;
    const { loadingPaymentIntent } = this.state;

    return (
      <Dialog open={visible}>
        <DialogTitle>Verifica pagamento ordine</DialogTitle>
        <DialogContent>
          {loading ? (
            <>
              <DialogContentText>Verifica pagamento in corso...</DialogContentText>
              <LinearProgress />
            </>
          ) : order.paymentStatus === EPaymentStatus.Paid ? (
            <DialogContentText>
              Abbiamo individuato con successo il tuo pagamento. Non è necessaria nessun'altra azione.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Non è stato possibile verificare il tuo pagamento. Probabilmente quest'ultimo non è andato a buon fine.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {loading ? (
            <Button disabled color="primary">
              Annulla
            </Button>
          ) : order.paymentStatus === EPaymentStatus.Paid ? (
            <Button onClick={handleCloseDialog} color="primary">
              Chiudi
            </Button>
          ) : (
            <>
              <Button disabled={loadingPaymentIntent} onClick={handleCloseDialog} color="secondary">
                Annulla
              </Button>
              <Button disabled={loadingPaymentIntent} onClick={this.tryPayment} color="primary">
                {loadingPaymentIntent ? "Elaboro schermata di pagamento..." : "Effettua nuovo pagamento"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

// --- Wrapper Funzionale per usare useNavigate ---
export default function Orders(props: any) {
  const navigate = useNavigate();
  return <OrdersClass {...props} navigate={navigate} />;
}

// --- GammaDocumentRowComponent ---
interface IGammaDocumentRowProps {
  document: GammaDocument;
}

const GammaDocumentRowComponent: React.FC<IGammaDocumentRowProps> = ({ document }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{dayjs(document.date).format("DD/MM/YYYY")}</TableCell>
        <TableCell>{document.documentNumber}</TableCell>
        <TableCell>{document.evaded === 1 ? "SI" : "NO"}</TableCell>
        <TableCell>{document.codDoc === "GIG-C-ORDINE-W" ? "WEB" : "COMMERCIALE"}</TableCell>
        <TableCell style={{ whiteSpace: "nowrap" }}>{euroPrint(document.total)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                Documento
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Codice Prodotto</TableCell>
                    <TableCell>Descrizione</TableCell>
                    <TableCell>Quantità</TableCell>
                    <TableCell>Costo Unitario</TableCell>
                    <TableCell>Totale Riga</TableCell>
                    <TableCell>Stato Evasione</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {document.rows
                    .sort((a, b) => a.progressive - b.progressive)
                    .map((item: GammaDocumentRow, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell component="th" scope="row">
                          {item.rowDescription.split("\r\n").map((line, idx) => (
                            <p style={{ lineHeight: 1 }} key={idx}>
                              {line}
                            </p>
                          ))}
                        </TableCell>
                        <TableCell>{item.quantity > 0 ? item.quantity : "//"}</TableCell>
                        <TableCell>
                          {euroPrint(item.quantity > 0 ? item.amount / item.quantity : item.amount)}
                        </TableCell>
                        <TableCell>{euroPrint(item.amount)}</TableCell>
                        <TableCell>
                          <GammaEvasionBadge isComplete={item.complete} />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

// --- OrderRow Component ---
interface OrderRowProps {
  order: Order;
  handlePayOrderCallback: (order: Order) => void;
  showConfiguration: (item: OrderItem) => void;
}

const OrderRow: React.FC<OrderRowProps> = ({ order, handlePayOrderCallback, showConfiguration }) => {
  const [open, setOpen] = React.useState(false);

  let orderTotal = order.items.reduce((total, item) => {
    return total + item.price * item.quantity + item.taxSiae * item.quantity + item.vatTotal * item.quantity;
  }, 0);
  orderTotal += order.shippingTotal * 1.22;

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{dayjs(order.createdOn).format("DD/MM/YYYY")}</TableCell>
        <TableCell>
          <PaymentMethodBadge paymentMethod={order.internalPaymentMethod} />
        </TableCell>
        <TableCell>{order.shippingAddress.fullAddress}</TableCell>
        <TableCell>{EnumTranslator.StatusEnumToString(order.status)}</TableCell>
        <TableCell style={{ whiteSpace: "nowrap" }}>{euroPrint(orderTotal)}</TableCell>
        <TableCell align="right">
          <Tooltip title="Verifica ed effettua pagamento">
            <IconButton disabled={true} onClick={() => handlePayOrderCallback(order)} size="small" color="primary">
              <Payment />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                Riepilogo Ordine
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Codice</TableCell>
                    <TableCell>Quantità</TableCell>
                    <TableCell>Prezzo</TableCell>
                    <TableCell>Siae</TableCell>
                    <TableCell>Iva</TableCell>
                    <TableCell>Totale Riga</TableCell>
                    <TableCell>Info</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        <Link to={`/shop/home/prodotto/${item.productId}`}>{item.name}</Link>
                      </TableCell>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>{euroPrint(item.price)}</TableCell>
                      <TableCell>{euroPrint(item.taxSiae)}</TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>{euroPrint(item.vatTotal)}</TableCell>
                      <TableCell>
                        {euroPrint(item.vatTotal * item.quantity + item.taxSiae * item.quantity + item.price * item.quantity)}
                      </TableCell>
                      <TableCell>
                        <Button disabled={item.orderItemConfigurations.length <= 0} onClick={() => showConfiguration(item)}>
                          Configurazione
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>SPEDIZIONE</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{euroPrint(order.shippingTotal)}</TableCell>
                    <TableCell>{euroPrint(0)}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{euroPrint(order.shippingTotal * 0.22)}</TableCell>
                    <TableCell>{euroPrint(order.shippingTotal * 1.22)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
